import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";

import { myLibraryApi, worksApi } from "../services/api";
import type { Work } from "../types";
import { RatingSelect } from "../components/RatingSelect";
import { WorkInfoCard } from "../components/WorkInfoCard";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;

export function MyLibraryAddBookPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { canCreate } = usePermissions();
  const [searchParams] = useSearchParams();

  const preselectedWorkId = searchParams.get("work_id");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  // Query for the preselected work if work_id is provided
  const { data: preselectedWork } = useQuery({
    queryKey: ["work", preselectedWorkId],
    queryFn: () => worksApi.get(preselectedWorkId as string),
    enabled: !!preselectedWorkId,
  });

  // Query for library entries to know which books are already added
  const { data: libraryEntriesData } = useQuery({
    queryKey: ["my-library-entries", "all-books"],
    queryFn: () =>
      myLibraryApi.listEntries({
        type: "book",
        page: 1,
        page_size: 1000,
      }),
  });

  const existingWorkIds = useMemo(() => {
    const items = libraryEntriesData?.items ?? [];
    return new Set(items.map((item) => String(item.work_id)));
  }, [libraryEntriesData]);

  // Set the form value when preselected work is loaded
  useEffect(() => {
    if (preselectedWork && preselectedWorkId) {
      form.setFieldValue("work_id", preselectedWorkId);
    }
  }, [preselectedWork, preselectedWorkId, form]);

  const { data: worksData, isLoading: worksLoading } = useQuery({
    queryKey: ["works", "my-library-add-book", search],
    queryFn: () =>
      worksApi.list({
        page: 1,
        page_size: 50,
        type: "book",
        search: search || undefined,
        sort: "title",
        order: "asc",
      }),
  });

  const workOptions = useMemo(() => {
    const items = worksData?.items ?? [];
    let filteredItems = items;

    if (!showAll) {
      filteredItems = items.filter((w) => !existingWorkIds.has(String(w.id)));
    }

    const options = filteredItems.map((w: Work) => ({
      value: w.id,
      label: w.title,
    }));

    // Add preselected work to options if not already present
    if (
      preselectedWork &&
      !options.some((opt) => opt.value === preselectedWork.id)
    ) {
      options.unshift({
        value: preselectedWork.id,
        label: preselectedWork.title,
      });
    }

    return options;
  }, [worksData, preselectedWork, showAll, existingWorkIds]);

  const mutation = useMutation({
    mutationFn: (payload: {
      work_id: string;
      finished_at: string;
      rating?: number;
      review?: string;
      private_notes?: string | null;
      storage_location?: string | null;
      price?: number;
      is_public?: boolean;
    }) => myLibraryApi.createEntry(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-library-years"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-stats"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-monthly"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-entries"] });
      message.success("Wpis dodany");
      navigate("/");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail || "Nie udało się dodać wpisu";
      message.error(errorMessage);
    },
  });

  const selectedWorkId = Form.useWatch("work_id", form) as string | undefined;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/my-library/add")}
          style={{ padding: 0 }}
        >
          Powrót
        </Button>
        <Title level={2} style={{ marginTop: 8, marginBottom: 0 }}>
          Dodaj książkę
        </Title>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            mutation.mutate({
              work_id: values.work_id as string,
              finished_at: values.finished_at as string,
              rating: values.rating as number | undefined,
              review: values.review as string | undefined,
              private_notes: values.private_notes as string | null | undefined,
              storage_location: values.storage_location as
                | string
                | null
                | undefined,
              price: values.price as number | undefined,
              is_public: values.is_public,
            });
          }}
          initialValues={{
            finished_at: new Date().toISOString().slice(0, 10),
            is_public: false,
          }}
        >
          <Form.Item
            name="work_id"
            label="Książka"
            rules={[{ required: true, message: "Wybierz książkę" }]}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <Select
                showSearch
                placeholder="Wybierz książkę"
                options={workOptions}
                loading={worksLoading}
                filterOption={false}
                onSearch={(v) => setSearch(v)}
                onChange={(value) => {
                  form.setFieldValue("work_id", value);
                  form.validateFields(["work_id"]);
                }}
                allowClear
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Space>
                  <Switch
                    size="small"
                    checked={showAll}
                    onChange={(checked) => setShowAll(checked)}
                  />
                  <span style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)" }}>
                    Pokaż już dodane
                  </span>
                </Space>
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate("/books/new")}
                  style={{ padding: 0 }}
                >
                  + Dodaj nową książkę do bazy
                </Button>
              </div>
            </div>
          </Form.Item>

          {selectedWorkId && <WorkInfoCard workId={selectedWorkId} />}

          <Form.Item
            name="finished_at"
            label="Data ukończenia"
            rules={[{ required: true, message: "Data jest wymagana" }]}
          >
            <Input type="date" />
          </Form.Item>

          <Form.Item name="price" label="Cena zakupu">
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: 200 }}
              addonAfter="zł"
            />
          </Form.Item>

          <Form.Item name="rating" label="Ocena">
            <RatingSelect />
          </Form.Item>

          <Form.Item name="review" label="Notatka">
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item name="private_notes" label="Prywatne notatki">
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="is_public"
            label="Opublikowany"
            valuePropName="checked"
            tooltip="Gdy włączone, recenzja będzie widoczna w publicznej bibliotece"
          >
            <Switch checkedChildren="Tak" unCheckedChildren="Nie" />
          </Form.Item>

          <Form.Item name="storage_location" label="Miejsce składowania">
            <Input />
          </Form.Item>

          <Form.Item
            style={{ marginTop: 24, marginBottom: 0, textAlign: "right" }}
          >
            <Space>
              <Button onClick={() => navigate("/")}>Anuluj</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={mutation.isPending}
                disabled={!canCreate}
                title={!canCreate ? "Brak uprawnień do dodawania" : undefined}
              >
                Dodaj
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default MyLibraryAddBookPage;
